/**
 * Maps Firebase error codes to user-friendly messages
 * Based on Firebase Auth error codes documentation
 */
export const firebaseErrorMessages: Record<string, string> = {
  // Authentication errors
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Invalid email or password. Please check your credentials.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password must be at least 6 characters long.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email but different sign-in credentials.',
  'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
  'auth/invalid-verification-id': 'Invalid verification ID. Please restart the verification process.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/requires-recent-login': 'Please sign in again to complete this action.',

  // Multi-factor authentication (TOTP)
  'auth/multi-factor-auth-required': 'Additional verification is required to sign in.',
  'auth/missing-verification-code': 'Please enter the 6-digit verification code.',
  'auth/second-factor-already-in-use': 'This authenticator is already enrolled on your account.',
  'auth/maximum-second-factor-count-exceeded':
    'You have reached the maximum number of authenticators.',
  'auth/unverified-email':
    'Your email address must be verified before enabling two-factor authentication.',
  'auth/unsupported-first-factor':
    'This account cannot use two-factor authentication.',
  'auth/totp-challenge-timeout': 'The verification timed out. Please try again.',
  'auth/network-request-failed': 'Network error. Please check your internet connection.',
  'auth/popup-closed-by-user': 'Sign-in popup was closed before completion.',
  'auth/cancelled-popup-request': 'Only one popup request is allowed at a time.',
  'auth/unauthorized-domain': 'This domain is not authorized for authentication.',

  // Firestore errors
  'permission-denied': 'You do not have permission to access this resource.',
  'not-found': 'The requested resource was not found.',
  'already-exists': 'This resource already exists.',
  'resource-exhausted': 'Resource quota exceeded. Please try again later.',
  'failed-precondition': 'Operation cannot be performed in the current state.',
  'aborted': 'Operation was aborted. Please try again.',
  'out-of-range': 'Invalid value provided.',
  'unimplemented': 'This feature is not yet available.',
  'internal': 'Internal server error. Please try again later.',
  'unavailable': 'Service temporarily unavailable. Please try again later.',
  'data-loss': 'Data loss detected. Please contact support.',
  'unauthenticated': 'Please sign in to continue.',
  'deadline-exceeded': 'Request took too long. Please try again.',
}

/**
 * Extracts user-friendly message from Firebase error
 * @param error - Firebase error object or any error
 * @returns User-friendly error message
 */
export function getFirebaseErrorMessage(error: any): string {
  // Handle Firebase Auth errors (error.code format: "auth/error-type")
  if (error?.code && typeof error.code === 'string') {
    const message = firebaseErrorMessages[error.code]
    if (message) return message
  }

  // Handle Firestore errors (error.code format: "error-type" without prefix)
  if (error?.code && error.code in firebaseErrorMessages) {
    return firebaseErrorMessages[error.code]
  }

  // Fallback to error message if available
  if (error?.message && typeof error.message === 'string') {
    // Don't return technical Firebase messages
    if (!error.message.includes('Firebase') && !error.message.includes('auth/')) {
      return error.message
    }
  }

  // Final fallback
  return 'An unexpected error occurred. Please try again.'
}

/**
 * Determines if a Firebase error is a network error
 * @param error - Firebase error object
 * @returns true if network-related error
 */
export function isNetworkError(error: any): boolean {
  return (
    error?.code === 'auth/network-request-failed' || error?.message?.toLowerCase().includes('network')
  )
}

/**
 * Determines if a Firebase error requires re-authentication
 * @param error - Firebase error object
 * @returns true if re-auth required
 */
export function requiresReauth(error: any): boolean {
  return error?.code === 'auth/requires-recent-login'
}
